<template>
  <div class="product-purchase-options-taobao">
    <!-- Guarantees (保障) -->
    <div class="option-section guarantees-info" v-if="guarantees && guarantees.length > 0">
      <span class="label">折價卷</span>
      <div class="value">
        <span v-for="guarantee in current_discount" :key="guarantee" class="guarantee-item">
          {{ guarantee }}
        </span>
      </div>
    </div>

    <!-- Payment Info (可选支付) -->
    <div class="option-section guarantees-info" v-if="paymentInfo && paymentInfo.length > 0">
      <span class="label">支付方式</span>
      <div class="value">
        <span v-for="payment in paymentInfo" :key="payment" class="guarantee-item">
          {{ payment }}
        </span>
      </div>
    </div>

    <!-- Delivery Info (配送) -->
    <div class="option-section guarantees-info" v-if="shippingCostInfo && shippingCostInfo.length > 0">
      <span class="label">配送方式</span>
      <div class="value">
        <span v-for="shipping in shippingCostInfo" :key="shipping" class="guarantee-item">
          {{ shipping }}
        </span>
      </div>
    </div>

    <!-- SKU Selection (e.g., 颜色分类) -->
    <div class="option-section sku-selection">
      <span class="label">{{ skuGroupName }}</span>
      <div class="value sku-options">
        <div
          v-for="sku in availableSkus"
          :key="sku.size"
          class="sku-option"
          :class="{
            active: selectedSkuId === sku.size,
            disabled: sku.stock === 0
          }"
          @click="selectSku(sku)"
        >
          <!-- <img v-if="sku.imageUrl" :src="sku.imageUrl" :alt="sku.name" class="sku-image" /> -->
          <span class="sku-name">{{ sku.size }}</span>
        </div>
      </div>
    </div>

    <!-- Quantity (数量) -->
    <div class="option-section quantity-selection">
      <span class="label">數量</span>
      <div class="value">
        <el-input-number
          v-model="quantity"
          :min="1"
          :disabled="!selectedSkuId || (selectedSku && selectedSku.stock === 0)"
          size="default"
          controls-position="right"
        />
        <span class="stock-status" v-if="selectedSku">
          {{ stockStatusText }}
        </span>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons-container">
      <div class="main-actions">
        <el-button
          class="action-btn coupon-buy-btn"
          @click="handleBuyNow"
          :disabled="!canProceed"
        >
          {{ buyNowButtonText }}
        </el-button>
        <el-button
          class="action-btn add-to-cart-btn"
          type="primary"
          @click="handleAddToCart"
          :disabled="!canProceed"
        >
          加入購物車
        </el-button>
      </div>
      <div class="secondary-actions">
        <el-button text @click="handleToggleFavorite" class="favorite-btn">
          <el-icon><Star v-if="!isFavorited" /><StarFilled v-if="isFavorited" /></el-icon>
          <span>{{ isFavorited ? '已收藏' : '收藏' }}</span>
        </el-button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { ArrowDown, InfoFilled, Star, StarFilled } from '@element-plus/icons-vue'; // Import necessary icons


const props = defineProps({
  productId: {
    type: [String, Number],
    required: true
  },
  price: {
    type: Number,
    default: null
  },
  availableSkus: { // Array of SKU objects
    type: Array,
    default: () => [] // Example: [{ id: 'sku1', name: '红色 S', imageUrl: 'url', stock: 10, price: 100 }, ...]
  },
  skuGroupName: { // Label for SKU section, e.g., "颜色分类"
    type: String,
    default: '选择规格'
  },
  Fstatus: { // Label for SKU section, e.g., "颜色分类"
    type: Boolean,
    default: false
  },
  shippingCostInfo: {
    type: Array,
    default: () => ['7-11到店取貨', '全家到店取貨', '宅配到府']
  },
  guarantees: {
    type: Array,
    default: () => []
  },
  paymentInfo: {
    type: Array,
    default: () => ['貨到付款', "信用卡", "ATM付款"]
  },
  isAuthenticated: { // 新增 prop，用于接收用户登录状态
    type: Boolean,
    required: true // 建议设为 required，确保父组件传递
  }
});

const emit = defineEmits(['addToCart', 'buyNow', 'toggleFavorite']);

const selectedSkuId = ref(null);
const quantity = ref(1);
const isFavorited = ref(props.Fstatus);
const current_discount = ref(props.guarantees)

watch(() => props.Fstatus, (newVal) => {
  isFavorited.value = newVal;
});

watch(() => props.guarantees, (newVal) => {
  current_discount.value = newVal;
});

const selectedSku = computed(() => {
  if (!selectedSkuId.value) return null;
  return props.availableSkus.find(sku => sku.size === selectedSkuId.value) || null;
});

const currentPrice = computed(() => {
  if (selectedSku.value && selectedSku.value.price !== undefined) {
    return selectedSku.value.price;
  }
  return props.price; // Fallback to base product price
});

const effectiveMaxQuantity = computed(() => {
  return selectedSku.value ? selectedSku.value.stock : 10; // Default max if no SKU or stock info
});

const stockStatusText = computed(() => {
  if (!selectedSku.value) return '';
  const stock = selectedSku.value.stock;
  if (stock === 0) return '无货';
//   if (stock < 10) return `库存${stock}件`;
  return '有货';
});

const canProceed = computed(() => {
  return selectedSku.value && selectedSku.value.stock > 0 && quantity.value > 0;
});

const selectSku = (sku) => {
  if (sku.stock === 0) {
    ElMessage.warning(`${sku.size} 已售罄`);
    return;
  }
  if (selectedSkuId.value === sku.size) {
    selectedSkuId.value = null; // Allow deselecting
  } else {
    selectedSkuId.value = sku.size;
    quantity.value = 1; // Reset quantity when SKU changes
  }
};

const checkAuthAndProceed = (actionCallback) => {
  if (!props.isAuthenticated) {
    ElMessage.warning('请先登录后再进行操作！');
    emit('requestLogin'); // 发出请求登录事件
    return false; // 表示操作未继续
  }
  actionCallback(); // 如果已登录，执行实际操作
  return true; // 表示操作已继续
};

const handleAddToCart = () => {
  checkAuthAndProceed(() => {
    if (!canProceed.value) {
      if (!selectedSkuId.value) ElMessage.warning('请选择' + props.skuGroupName);
      else ElMessage.warning('商品库存不足或购买数量无效');
      return;
    }
    emit('addToCart', {
      productId: props.productId,
      skuId: selectedSkuId.value,
      skuName: selectedSku.value?.size,
      quantity: quantity.value,
      price: currentPrice.value,
    });
  });
};

const handleBuyNow = () => {
  checkAuthAndProceed(() => { 
    if (!canProceed.value) {
      if (!selectedSkuId.value) ElMessage.warning('请选择' + props.skuGroupName);
      else ElMessage.warning('商品库存不足或购买数量无效');
      return;
    }
    emit('buyNow', {
      skuId: selectedSkuId.value,
      skuName: selectedSku.value?.size,
      quantity: quantity.value,
      price: currentPrice.value,
      normal_price: selectedSku.value.normal_price,
    });
  });
};

const buyNowButtonText = computed(() => {
  // Check if there are any items in the 'coupons' array
  const hasCoupons = current_discount.value && current_discount.value.length > 0;
  return hasCoupons ? '領券購買' : '購買';
});

const handleToggleFavorite = () => {
  checkAuthAndProceed(() => { 
    const newFavoriteStatus = !isFavorited.value;
    isFavorited.value = newFavoriteStatus;
    emit('toggleFavorite', { productId: props.productId, isFavorited: newFavoriteStatus });
    // 注意：实际的收藏状态持久化应在父组件或 Vuex action 中处理，并考虑API失败回滚
  });
};
// Auto-select first available SKU if any
watch(() => props.availableSkus, (newSkus) => {
    if (newSkus && newSkus.length > 0 && !selectedSkuId.value) {
        const firstAvailableSku = newSkus.find(s => s.stock > 0);
        if (firstAvailableSku) {
            // selectedSkuId.value = firstAvailableSku.id; // Optionally auto-select
        }
    }
}, { immediate: true });

</script>

<style scoped>
.product-purchase-options-taobao {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 12px;
  color: #333;
  background-color: #fff; /* Taobao sections are often on white or very light gray */
  padding: 10px 15px; /* Overall padding */
}

.option-section {
  display: flex;
  align-items: flex-start; /* Align label to top with multi-line values */
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0; /* Lighter separator */
}
.option-section:last-of-type {
    border-bottom: none;
    margin-bottom: 15px;
}


.label {
  min-width: 60px; /* Adjusted for Chinese labels */
  color: #888;
  margin-right: 10px;
  padding-top: 2px; /* Small adjustment for alignment */
  font-size: 12px;
}

.value {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

/* Delivery Info */
.delivery-info .value {
  gap: 15px;
}
.delivery-item {
  color: #333;
}
.delivery-info .el-icon {
  vertical-align: middle;
  cursor: pointer;
}

/* Guarantees Info */
.guarantees-info .value {
    gap: 15px;
}
.guarantee-item {
  color: #666;
  padding-right: 10px;
  border-right: 1px solid #eee;
}
.guarantee-item:last-child {
    border-right: none;
}


/* Payment Info Static */
.payment-info-static .el-icon {
    vertical-align: middle;
    color: #aaa;
    cursor: help;
}

/* SKU Selection */
.sku-selection .label {
  padding-top: 8px; /* Align with SKU items better */
}
.sku-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.sku-option {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #f7f7f7;
}
.sku-option:hover {
  border-color: #409EFF;
}
.sku-option.active {
  border-color: #409EFF;
  background-color: #E6F7FF; /* Light orange for active */
  color: #1890FF;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}
.sku-option.disabled {
  color: #bbb;
  border-color: #eee;
  background-color: #f9f9f9;
  cursor: not-allowed;
  text-decoration: line-through;
}
.sku-option.disabled:hover {
    border-color: #eee;
}
.sku-image {
  width: 24px;
  height: 24px;
  object-fit: cover;
  margin-right: 6px;
  border: 1px solid #eee; /* Slight border for image */
}
.sku-name {
  font-size: 12px;
}

/* Quantity Selection */
.quantity-selection .el-input-number {
  width: 100px;
  height: 28px; /* Match Taobao's typical height */
}
.quantity-selection .el-input-number .el-input__inner {
  height: 28px;
  line-height: 28px;
}
.stock-status {
  margin-left: 10px;
  color: #888;
}

/* Action Buttons */
.action-buttons-container {
  display: flex;
  align-items: center;
  margin-top: 20px;
  padding-top: 15px;
}
.main-actions {
  display: flex;
  flex-grow: 1;
  height: 42px; /* Typical Taobao button height */
}
.el-button {
  margin-left: 0% !important;
}
.action-btn {
  flex-grow: 1;
  border-radius: 0;
  height: 100%;
  font-size: 14px;
  font-weight: bold;
}
.coupon-buy-btn {
  background: linear-gradient(to right, #409EFF, #1890FF); /* Orange gradient */
  color: white;
  border: none;
  border-top-left-radius: 21px; /* Half of height */
  border-bottom-left-radius: 21px;
}
.coupon-buy-btn:hover {
  background: linear-gradient(to right, #409EFF, #1890FF);
  color: white;
}
.add-to-cart-btn {
  background: linear-gradient(to right, #409EFF, #1890FF); /* Yellow/Orange gradient */
  color: white;
  border: none;
  border-top-right-radius: 21px;
  border-bottom-right-radius: 21px;
  margin-left: -1px; 
}
.add-to-cart-btn:hover {
  background: linear-gradient(to right, #3a8ee6, #1372d1);
  color: white;
}
.action-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
.coupon-buy-btn:disabled {
  background: #ccc;
}
.add-to-cart-btn:disabled {
  background: #ddd;
  color: #888;
}


.secondary-actions {
  margin-left: 20px;
}
.favorite-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #666;
  font-size: 10px; /* Smaller text for favorite */
  padding: 0;
  min-height: auto;
}
.favorite-btn .el-icon {
  font-size: 18px; /* Icon size */
  margin-bottom: 0px;
}
.favorite-btn:hover, .favorite-btn.is-active {
  color: #409EFF;
}

</style>